import type * as ents from '@attain-apps/entities'
import type * as store from '@attain-apps/fe/web/main/lib/store'

import type { Feature } from './feature'
import type { State } from './state'

export class Selectors {
  /**
   * Most recent {@link ents.core.quiver.Evaluation} from {@link State.evaluationChain}
   */
  public readonly currentEvaluation: store.selectors.BoundSelector<
    ents.core.quiver.Evaluation | undefined
  >
  /**
   * Everything but the most recent {@link ents.core.quiver.Evaluation} from
   * {@link State.evaluationChain}
   */
  public readonly previousEvaluations: store.selectors.BoundSelector<
    readonly ents.core.quiver.Evaluation.Incomplete[]
  >
  /** Length of {@link State.evaluationChain} */
  public readonly evaluationCount: store.selectors.BoundSelector<number>

  /** @see {@link State.isEvaluating} */
  public readonly isEvaluating: store.selectors.BoundSelector<boolean>
  /** @see {@link State.evaluationError} */
  public readonly evaluationError: store.selectors.BoundSelector<
    Error | undefined
  >
  /** @see {@link State.evaluationError} */
  public readonly evaluationErrorMessage: store.selectors.BoundSelector<
    string | undefined
  >

  /** @see {@link State.isDeploying} */
  public readonly isDeploying: store.selectors.BoundSelector<boolean>
  /** @see {@link State.deploymentError} */
  public readonly deploymentError: store.selectors.BoundSelector<
    Error | undefined
  >
  /** @see {@link State.deploymentError} */
  public readonly deploymentErrorMessage: store.selectors.BoundSelector<
    string | undefined
  >

  public readonly variableValues: store.selectors.BoundSelector<ents.core.dms.data.query.Variable.Lookup>
  public readonly getVariableValue

  constructor(feature: Feature) {
    const evaluationChain = feature.state.project(
      state => state.evaluationChain,
    )
    this.currentEvaluation = evaluationChain.project(chain =>
      chain.length ? chain[chain.length - 1] : undefined,
    )
    this.previousEvaluations = evaluationChain.project(
      chain =>
        // NOTE: only the last evaluation could potentially be complete, and we
        // are excluding that evaluation, so everything that is returned is
        // incomplete
        chain.slice(
          0,
          -1,
        ) satisfies readonly ents.core.quiver.Evaluation[] as readonly ents.core.quiver.Evaluation.Incomplete[],
    )
    this.evaluationCount = evaluationChain.project(chain => chain.length)

    this.isEvaluating = feature.state.project(state => state.isEvaluating)
    this.evaluationError = feature.state.project(state => state.evaluationError)
    this.evaluationErrorMessage = this.evaluationError.project(
      error => error?.message,
    )

    this.isDeploying = feature.state.project(state => state.isDeploying)
    this.deploymentError = feature.state.project(state => state.deploymentError)
    this.deploymentErrorMessage = this.deploymentError.project(
      error => error?.message,
    )

    this.variableValues = feature.state.project(state => state.variableValues)

    this.getVariableValue = feature.createSelectorMethod((id: string) =>
      this.variableValues.project(lookup => lookup[id]),
    )
  }
}
